import { useEffect, useState, useRef } from 'react';
import { Scan, Search, Package, ArrowRight, User, MapPin, Clock, Camera } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Separator } from '@/components/ui/separator';
import { partInstanceAPI } from '@/lib/api';
import { cn, formatDateTime, getStatusColor } from '@/lib/utils';
import { toast } from 'sonner';

const TimelineEvent = ({ event, isLast }) => {
  const eventIcons = {
    received: Package,
    pickup: User,
    transfer: ArrowRight,
    assigned: User,
    installed: Package,
    returned: ArrowRight,
    lost: Package,
  };
  const Icon = eventIcons[event.event_type] || Package;

  return (
    <div className="flex gap-4">
      <div className="flex flex-col items-center">
        <div className={cn(
          "w-10 h-10 rounded-full flex items-center justify-center",
          event.event_type === 'received' ? "bg-emerald-100 text-emerald-600" :
          event.event_type === 'installed' ? "bg-green-100 text-green-600" :
          event.event_type === 'lost' ? "bg-red-100 text-red-600" :
          "bg-blue-100 text-blue-600"
        )}>
          <Icon className="w-5 h-5" />
        </div>
        {!isLast && <div className="w-0.5 h-full bg-slate-200 mt-2" />}
      </div>
      <div className="flex-1 pb-6">
        <div className="flex items-center gap-2 mb-1">
          <p className="font-medium text-slate-900 capitalize">
            {event.event_type.replace('_', ' ')}
          </p>
          <span className="text-xs text-slate-500">
            {formatDateTime(event.created_at)}
          </span>
        </div>
        <div className="text-sm text-slate-600 space-y-1">
          {event.from_user_name && (
            <p className="flex items-center gap-1">
              <User className="w-3 h-3" />
              From: {event.from_user_name}
            </p>
          )}
          {event.to_user_name && (
            <p className="flex items-center gap-1">
              <User className="w-3 h-3" />
              To: {event.to_user_name}
            </p>
          )}
          {event.notes && (
            <p className="text-slate-500 italic">"{event.notes}"</p>
          )}
        </div>
        {event.created_by_name && (
          <p className="text-xs text-slate-400 mt-1">
            Recorded by {event.created_by_name}
          </p>
        )}
      </div>
    </div>
  );
};

const PartDetail = ({ instance }) => {
  if (!instance) return null;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <p className="font-mono text-lg font-bold text-slate-900">{instance.barcode}</p>
          <p className="text-slate-600">{instance.part?.part_number} - {instance.part?.description}</p>
        </div>
        <Badge className={cn("text-sm", getStatusColor(instance.status))}>
          {instance.status.replace('_', ' ')}
        </Badge>
      </div>

      {/* Current State */}
      <Card className="bg-slate-50 border-slate-200">
        <CardContent className="p-4">
          <h3 className="font-semibold text-slate-900 mb-3">Current State</h3>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <p className="text-slate-500">Current Owner</p>
              <p className="font-medium flex items-center gap-1">
                <User className="w-4 h-4 text-slate-400" />
                {instance.current_user_name || 'Unassigned'}
              </p>
            </div>
            <div>
              <p className="text-slate-500">Location</p>
              <p className="font-medium flex items-center gap-1">
                <MapPin className="w-4 h-4 text-slate-400" />
                {instance.current_location_name || 'Unknown'}
              </p>
            </div>
            {instance.serial_number && (
              <div>
                <p className="text-slate-500">Serial Number</p>
                <p className="font-mono">{instance.serial_number}</p>
              </div>
            )}
            {instance.lot_number && (
              <div>
                <p className="text-slate-500">Lot Number</p>
                <p className="font-mono">{instance.lot_number}</p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Timeline */}
      <div>
        <h3 className="font-semibold text-slate-900 mb-4">Custody Timeline</h3>
        {instance.timeline && instance.timeline.length > 0 ? (
          <div className="pl-2">
            {instance.timeline.map((event, index) => (
              <TimelineEvent 
                key={event.id} 
                event={event} 
                isLast={index === instance.timeline.length - 1}
              />
            ))}
          </div>
        ) : (
          <p className="text-slate-500 text-sm">No custody events recorded</p>
        )}
      </div>
    </div>
  );
};

export default function PartTracePage() {
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [instance, setInstance] = useState(null);
  const [recentSearches, setRecentSearches] = useState([]);
  const [scanning, setScanning] = useState(false);
  const videoRef = useRef(null);
  const streamRef = useRef(null);

  const handleSearch = async (query = searchQuery) => {
    if (!query.trim()) return;
    
    setLoading(true);
    setInstance(null);
    
    try {
      // Try to find by barcode first
      const response = await partInstanceAPI.getByBarcode(query.trim());
      setInstance(response.data);
      
      // Add to recent searches
      setRecentSearches(prev => {
        const filtered = prev.filter(s => s !== query.trim());
        return [query.trim(), ...filtered].slice(0, 5);
      });
    } catch (error) {
      if (error.response?.status === 404) {
        toast.error('Part not found. Try scanning the barcode or entering the full ID.');
      } else {
        toast.error('Failed to fetch part details');
      }
    } finally {
      setLoading(false);
    }
  };

  const startScanning = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: { facingMode: 'environment' } 
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
      setScanning(true);
      toast.info('Point camera at barcode to scan');
    } catch (error) {
      toast.error('Unable to access camera. Please enter barcode manually.');
    }
  };

  const stopScanning = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    setScanning(false);
  };

  useEffect(() => {
    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
    };
  }, []);

  return (
    <div className="space-y-6" data-testid="part-trace-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Part Trace</h1>
        <p className="text-slate-600">Track any part by barcode or ID</p>
      </div>

      {/* Search */}
      <Card>
        <CardContent className="p-4">
          <div className="flex gap-3">
            <div className="relative flex-1">
              <Scan className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <Input
                placeholder="Enter barcode or part ID (e.g., TRK-XXXX)"
                className="pl-11 h-12 text-lg font-mono"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value.toUpperCase())}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                data-testid="trace-search-input"
              />
            </div>
            <Button 
              size="lg" 
              onClick={() => handleSearch()}
              disabled={loading || !searchQuery.trim()}
              data-testid="trace-search-btn"
            >
              <Search className="w-5 h-5 mr-2" />
              Search
            </Button>
            <Button
              size="lg"
              variant="outline"
              onClick={scanning ? stopScanning : startScanning}
              data-testid="scan-camera-btn"
            >
              <Camera className="w-5 h-5" />
            </Button>
          </div>

          {/* Camera Preview */}
          {scanning && (
            <div className="mt-4 relative">
              <video
                ref={videoRef}
                autoPlay
                playsInline
                className="w-full max-w-md mx-auto rounded-lg border-2 border-dashed border-slate-300"
              />
              <p className="text-center text-sm text-slate-500 mt-2">
                Camera scanning active. Enter barcode manually if auto-scan doesn't work.
              </p>
            </div>
          )}

          {/* Recent Searches */}
          {recentSearches.length > 0 && !instance && (
            <div className="mt-4">
              <p className="text-sm text-slate-500 mb-2">Recent searches:</p>
              <div className="flex flex-wrap gap-2">
                {recentSearches.map((search) => (
                  <Button
                    key={search}
                    variant="outline"
                    size="sm"
                    className="font-mono"
                    onClick={() => {
                      setSearchQuery(search);
                      handleSearch(search);
                    }}
                  >
                    {search}
                  </Button>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Loading */}
      {loading && (
        <Card>
          <CardContent className="p-6">
            <Skeleton className="h-8 w-48 mb-4" />
            <Skeleton className="h-4 w-64 mb-6" />
            <Skeleton className="h-32 w-full mb-4" />
            <Skeleton className="h-48 w-full" />
          </CardContent>
        </Card>
      )}

      {/* Result */}
      {instance && !loading && (
        <Card>
          <CardContent className="p-6">
            <PartDetail instance={instance} />
          </CardContent>
        </Card>
      )}

      {/* Empty State */}
      {!instance && !loading && (
        <Card>
          <CardContent className="py-16 text-center">
            <Scan className="w-16 h-16 mx-auto text-slate-300 mb-4" />
            <h3 className="text-lg font-medium text-slate-900 mb-2">Trace a Part</h3>
            <p className="text-slate-500 max-w-md mx-auto">
              Enter a barcode, serial number, or part ID to see the complete custody timeline 
              and current location of any part in the system.
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
